import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import { leaveAPI } from '../lib/api';
import { formatDate, getStatusBadgeClass, capitalizeFirst } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Plus, Calendar as CalendarIcon, Clock, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

// Leave type colors
const LEAVE_TYPE_COLORS = {
  casual: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-100' },
  sick: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-100' },
  earned: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-100' },
  compensatory: { bg: 'bg-purple-500', text: 'text-purple-600', light: 'bg-purple-100' },
  unpaid: { bg: 'bg-slate-500', text: 'text-slate-600', light: 'bg-slate-100' },
};

// Calendar blocked date styles - defined outside component to prevent re-creation
const BLOCKED_MODIFIER_STYLES = {
  backgroundColor: 'rgb(254 226 226)',
  color: 'rgb(185 28 28)',
  fontWeight: 'bold'
};

// Helper functions to replace nested ternaries
const getLeaveStatusBgClass = (status) => {
  switch (status) {
    case 'approved': return 'bg-emerald-100';
    case 'rejected': return 'bg-red-100';
    case 'cancelled': return 'bg-slate-100';
    default: return 'bg-amber-100';
  }
};

const renderLeaveStatusIcon = (status) => {
  switch (status) {
    case 'approved':
      return <CheckCircle className="w-5 h-5 text-emerald-600" />;
    case 'rejected':
      return <XCircle className="w-5 h-5 text-red-600" />;
    case 'cancelled':
      return <XCircle className="w-5 h-5 text-slate-500" />;
    default:
      return <Clock className="w-5 h-5 text-amber-600" />;
  }
};

const MyLeaves = () => {
  const { user } = useAuth();
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('applications');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employee_id]);

  const fetchData = async () => {
    try {
      const leaveRes = await leaveAPI.getAll();
      setLeaveApplications(leaveRes.data);
      
      if (user?.employee_id) {
        const balanceRes = await leaveAPI.getBalance(user.employee_id);
        setLeaveBalance(balanceRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Get blocked dates (pending, verified, approved leaves)
  const blockedDates = useMemo(() => {
    const dates = new Map(); // dateStr -> leave info
    const activeLeaves = leaveApplications.filter(l => 
      ['pending', 'verified', 'approved'].includes(l.status)
    );
    
    activeLeaves.forEach(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        if (!dates.has(dateStr)) {
          dates.set(dateStr, []);
        }
        dates.get(dateStr).push(leave);
      }
    });
    
    return dates;
  }, [leaveApplications]);

  // Check if a date is blocked
  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.has(dateStr);
  };

  // Get blocked dates as array for calendar
  const blockedDatesArray = useMemo(() => {
    return Array.from(blockedDates.keys()).map(d => new Date(d));
  }, [blockedDates]);

  // Check if selected date range has conflicts
  const hasConflicts = useMemo(() => {
    if (!leaveForm.start_date || !leaveForm.end_date) return false;
    
    const start = new Date(leaveForm.start_date);
    const end = new Date(leaveForm.end_date);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (isDateBlocked(d)) return true;
    }
    return false;
  }, [leaveForm.start_date, leaveForm.end_date, blockedDates]);

  // Get conflicts for display
  const conflicts = useMemo(() => {
    if (!leaveForm.start_date || !leaveForm.end_date) return [];
    
    const conflictList = [];
    const start = new Date(leaveForm.start_date);
    const end = new Date(leaveForm.end_date);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      if (blockedDates.has(dateStr)) {
        const leaves = blockedDates.get(dateStr);
        leaves.forEach(leave => {
          if (!conflictList.find(c => c.id === leave.id)) {
            conflictList.push(leave);
          }
        });
      }
    }
    return conflictList;
  }, [leaveForm.start_date, leaveForm.end_date, blockedDates]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (hasConflicts) {
      toast.error('Selected dates conflict with existing leave applications');
      return;
    }
    
    setSubmitting(true);
    try {
      await leaveAPI.apply(leaveForm);
      toast.success('Leave application submitted');
      setIsModalOpen(false);
      setLeaveForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply for leave');
    } finally {
      setSubmitting(false);
    }
  };

  // Get leave info for a calendar date
  const getLeavesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.get(dateStr) || [];
  };

  const pendingLeaves = leaveApplications.filter((l) => l.status === 'pending');
  const approvedLeaves = leaveApplications.filter((l) => l.status === 'approved');
  const rejectedLeaves = leaveApplications.filter((l) => l.status === 'rejected');
  const cancelledLeaves = leaveApplications.filter((l) => l.status === 'cancelled');

  // Check if leave can be cancelled by employee (before start date)
  const canEmployeeCancel = (leave) => {
    if (!['pending', 'verified', 'approved'].includes(leave.status)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(leave.start_date);
    startDate.setHours(0, 0, 0, 0);
    return today < startDate;
  };

  const handleCancelLeave = async (leaveId) => {
    if (!confirm('Are you sure you want to cancel this leave application?')) return;
    
    try {
      await leaveAPI.cancel(leaveId);
      toast.success('Leave cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  // Calculate days between dates
  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  };

  const leaveDays = calculateDays(leaveForm.start_date, leaveForm.end_date);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="my-leaves-page">
      <Header title="My Leaves" subtitle="Manage your leave applications" />

      <div className="p-8">
        {/* Leave Balance Cards */}
        {leaveBalance && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="dashboard-card stat-accent-primary">
              <p className="text-xs font-medium text-slate-500 uppercase">Casual Leave</p>
              <p className="text-2xl font-heading font-semibold text-slate-900 mt-1">
                {(leaveBalance.casual_leave || 12) - (leaveBalance.casual_leave_used || 0)}
                <span className="text-sm font-normal text-slate-400"> / {leaveBalance.casual_leave || 12}</span>
              </p>
            </div>
            <div className="dashboard-card stat-accent-success">
              <p className="text-xs font-medium text-slate-500 uppercase">Sick Leave</p>
              <p className="text-2xl font-heading font-semibold text-emerald-600 mt-1">
                {(leaveBalance.sick_leave || 12) - (leaveBalance.sick_leave_used || 0)}
                <span className="text-sm font-normal text-slate-400"> / {leaveBalance.sick_leave || 12}</span>
              </p>
            </div>
            <div className="dashboard-card stat-accent-accent">
              <p className="text-xs font-medium text-slate-500 uppercase">Earned Leave</p>
              <p className="text-2xl font-heading font-semibold text-terra-500 mt-1">
                {(leaveBalance.earned_leave || 15) - (leaveBalance.earned_leave_used || 0)}
                <span className="text-sm font-normal text-slate-400"> / {leaveBalance.earned_leave || 15}</span>
              </p>
            </div>
            <div className="dashboard-card stat-accent-warning">
              <p className="text-xs font-medium text-slate-500 uppercase">Unpaid Used</p>
              <p className="text-2xl font-heading font-semibold text-amber-600 mt-1">
                {leaveBalance.unpaid_leave_used || 0}
                <span className="text-sm font-normal text-slate-400"> days</span>
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="applications" className="gap-2">
                <Clock className="w-4 h-4" />
                Applications
              </TabsTrigger>
              <TabsTrigger value="calendar" className="gap-2">
                <CalendarIcon className="w-4 h-4" />
                My Calendar
              </TabsTrigger>
            </TabsList>
            <Button onClick={() => setIsModalOpen(true)} data-testid="apply-leave-btn">
              <Plus className="w-4 h-4 mr-2" />
              Apply for Leave
            </Button>
          </div>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="all">All ({leaveApplications.length})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({pendingLeaves.length})</TabsTrigger>
                <TabsTrigger value="approved">Approved ({approvedLeaves.length})</TabsTrigger>
                <TabsTrigger value="rejected">Rejected ({rejectedLeaves.length})</TabsTrigger>
                <TabsTrigger value="cancelled">Cancelled ({cancelledLeaves.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                <LeaveList applications={leaveApplications} canCancel={canEmployeeCancel} onCancel={handleCancelLeave} />
              </TabsContent>
              <TabsContent value="pending">
                <LeaveList applications={pendingLeaves} canCancel={canEmployeeCancel} onCancel={handleCancelLeave} />
              </TabsContent>
              <TabsContent value="approved">
                <LeaveList applications={approvedLeaves} canCancel={canEmployeeCancel} onCancel={handleCancelLeave} />
              </TabsContent>
              <TabsContent value="rejected">
                <LeaveList applications={rejectedLeaves} canCancel={() => false} onCancel={handleCancelLeave} />
              </TabsContent>
              <TabsContent value="cancelled">
                <LeaveList applications={cancelledLeaves} canCancel={() => false} onCancel={handleCancelLeave} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">My Leave Calendar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedCalendarDate}
                      onSelect={setSelectedCalendarDate}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      className="rounded-md border"
                      modifiers={{
                        blocked: blockedDatesArray
                      }}
                      modifiersStyles={{
                        blocked: BLOCKED_MODIFIER_STYLES
                      }}
                    />
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
                      <span className="text-sm text-slate-600">Applied/Approved</span>
                    </div>
                    {Object.entries(LEAVE_TYPE_COLORS).map(([type, colors]) => (
                      <div key={type} className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colors.bg}`}></div>
                        <span className="text-sm text-slate-600 capitalize">{type}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Selected Date Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {selectedCalendarDate 
                      ? formatDate(selectedCalendarDate.toISOString().split('T')[0])
                      : 'Select a Date'
                    }
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedCalendarDate ? (
                    <div className="text-center py-8 text-slate-500">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Click on a date to view details</p>
                    </div>
                  ) : getLeavesForDate(selectedCalendarDate).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50 text-emerald-500" />
                      <p>No leaves on this date</p>
                      <Button 
                        className="mt-4" 
                        size="sm"
                        onClick={() => {
                          const dateStr = selectedCalendarDate.toISOString().split('T')[0];
                          setLeaveForm(prev => ({ ...prev, start_date: dateStr, end_date: dateStr }));
                          setIsModalOpen(true);
                        }}
                      >
                        Apply Leave for This Date
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getLeavesForDate(selectedCalendarDate).map((leave) => (
                        <div 
                          key={leave.id}
                          className={`p-3 rounded-lg ${LEAVE_TYPE_COLORS[leave.leave_type]?.light || 'bg-slate-100'}`}
                        >
                          <div className="flex items-center justify-between">
                            <Badge variant="outline" className={LEAVE_TYPE_COLORS[leave.leave_type]?.text}>
                              {capitalizeFirst(leave.leave_type)}
                            </Badge>
                            <span className={getStatusBadgeClass(leave.status)}>
                              {capitalizeFirst(leave.status)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-2">
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">{leave.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Apply Leave Modal with Blocked Dates */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg" data-testid="leave-modal" aria-describedby="apply-leave-description">
          <DialogHeader>
            <DialogTitle className="font-heading">Apply for Leave</DialogTitle>
            <p id="apply-leave-description" className="text-sm text-slate-500">
              Submit your leave request. Dates with existing applications are highlighted.
            </p>
          </DialogHeader>

          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select
                value={leaveForm.leave_type}
                onValueChange={(val) => setLeaveForm({ ...leaveForm, leave_type: val })}
              >
                <SelectTrigger data-testid="select-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="earned">Earned Leave</SelectItem>
                  <SelectItem value="compensatory">Compensatory Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  required
                  data-testid="input-start-date"
                  className={leaveForm.start_date && isDateBlocked(new Date(leaveForm.start_date + 'T00:00:00')) ? 'border-red-500' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  required
                  data-testid="input-end-date"
                  min={leaveForm.start_date}
                  className={leaveForm.end_date && isDateBlocked(new Date(leaveForm.end_date + 'T00:00:00')) ? 'border-red-500' : ''}
                />
              </div>
            </div>

            {/* Days calculation */}
            {leaveDays > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <CalendarIcon className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">
                  Duration: <strong>{leaveDays} day{leaveDays > 1 ? 's' : ''}</strong>
                </span>
              </div>
            )}

            {/* Conflict Warning */}
            {hasConflicts && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                  <AlertCircle className="w-4 h-4" />
                  Date Conflict Detected
                </div>
                <p className="text-sm text-red-600 mb-2">
                  Selected dates overlap with existing leave applications:
                </p>
                <ul className="text-sm text-red-600 space-y-1">
                  {conflicts.map(leave => (
                    <li key={leave.id}>
                      • {capitalizeFirst(leave.leave_type)} ({formatDate(leave.start_date)} - {formatDate(leave.end_date)}) - {capitalizeFirst(leave.status)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Blocked dates info */}
            {blockedDates.size > 0 && !hasConflicts && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-amber-700">
                  You have {blockedDates.size} date(s) with existing leave applications. 
                  Select different dates to avoid conflicts.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder="Enter reason for leave"
                required
                data-testid="input-reason"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || hasConflicts}
                data-testid="submit-leave-btn"
              >
                {submitting ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeaveList = ({ applications, canCancel = () => false, onCancel }) => {
  if (applications.length === 0) {
    return (
      <div className="empty-state py-12 dashboard-card">
        <CalendarIcon className="w-12 h-12 text-slate-300" />
        <h3>No leave applications</h3>
        <p>Your leave applications will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((leave) => {
        const showCancelBtn = canCancel(leave);
        
        return (
          <div
            key={leave.id}
            className="dashboard-card flex items-start justify-between"
            data-testid={`leave-card-${leave.id}`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${getLeaveStatusBgClass(leave.status)}`}>
                {renderLeaveStatusIcon(leave.status)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-slate-900 capitalize">{leave.leave_type} Leave</h4>
                  <Badge variant="outline" className={LEAVE_TYPE_COLORS[leave.leave_type]?.text}>
                    {leave.days} day{leave.days > 1 ? 's' : ''}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 mt-1">{leave.reason}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                  <span>{formatDate(leave.start_date)} - {formatDate(leave.end_date)}</span>
                </div>
                {leave.rejection_reason && (
                  <p className="mt-2 text-sm text-red-600">
                    Rejected: {leave.rejection_reason}
                  </p>
                )}
                {leave.cancellation_reason && (
                  <p className="mt-2 text-sm text-orange-600">
                    Cancelled: {leave.cancellation_reason}
                  </p>
                )}
                {leave.cancellation_note && !leave.cancellation_reason && (
                  <p className="mt-2 text-sm text-slate-500">
                    Note: {leave.cancellation_note}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {showCancelBtn && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => onCancel(leave.id)}
                  data-testid={`cancel-leave-${leave.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              )}
              <span className={getStatusBadgeClass(leave.status)}>
                {capitalizeFirst(leave.status)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MyLeaves;
